import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, Linkedin, Facebook, Twitter, Instagram, MapPin, Shield, Award, Users, FileText, CheckCircle2 } from "lucide-react";
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  async function handleSubscribe(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.newsletter({ 
        email, 
        contact_source: 'footer',
        consent_timestamp: new Date().toISOString() 
      });
      toast({
        title: "Subscribed!",
        description: "Thanks! You'll receive our latest financial insights.",
        variant: "success"
      });
      setEmail('');
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || 'Subscription failed. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  }

  const services = [
    { name: "Retirement Planning", href: "/#services" },
    { name: "Financial Protection", href: "/#services" },
    { name: "Wealth Building", href: "/#services" },
    { name: "Tax Strategies", href: "/#services" },
    { name: "Goal Planning", href: "/#services" },
    { name: "Family Protection", href: "/#services" }
  ];

  const resources = [
    { name: "Knowledge Hub & Blog", href: "/blog" },
    { name: "Live Masterclasses", href: "/webinar" },
    { name: "State Licensing & NPN", href: "/licensing" },
    { name: "Regulatory Disclaimer", href: "/disclaimer" }
  ];

  const socialLinks = [
    { icon: <Linkedin className="h-5 w-5" />, href: "https://www.linkedin.com/in/meet-priyanka-karn/", label: "LinkedIn" },
    { icon: <Facebook className="h-5 w-5" />, href: "https://www.facebook.com/people/Grow-And-Stay-Rich/61572905996150/", label: "Facebook" },
    { icon: <Twitter className="h-5 w-5" />, href: "https://x.com/GrowAndStayRich", label: "Twitter" },
    { icon: <Instagram className="h-5 w-5" />, href: "https://www.instagram.com/growandstayrich/", label: "Instagram" }
  ];

  return (
    <footer className="relative bg-[#070E1B] border-t border-[#C9A84C]/30 text-white overflow-hidden">
      {/* ── Ambient Glow Lighting ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-[#C9A84C]/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center">
                <img 
                  src="/images/logo/logo-horizontal-white.svg" 
                  alt="Grow & Stay Rich Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-opensans">
                Your trusted financial advisory network helping business leaders and families build lasting wealth, protect assets, and secure a tax-free future through strategic planning and Living Benefits.
              </p>
              
              {/* Trust & Licensing Badges */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Link to="/licensing" className="flex items-center gap-2 text-xs font-semibold text-[#E8D4A0] bg-[#162236] border border-[#C9A84C]/30 px-3 py-2 rounded-xl hover:border-[#C9A84C] transition-all">
                  <Shield className="h-4 w-4 text-[#C9A84C] shrink-0" />
                  <span>NPN: 21450620</span>
                </Link>
                <div className="flex items-center gap-2 text-xs font-semibold text-[#E8D4A0] bg-[#162236] border border-[#C9A84C]/30 px-3 py-2 rounded-xl">
                  <Award className="h-4 w-4 text-[#C9A84C] shrink-0" />
                  <span>15+ Yrs Leadership</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-[#E8D4A0] bg-[#162236] border border-[#C9A84C]/30 px-3 py-2 rounded-xl">
                  <Users className="h-4 w-4 text-[#C9A84C] shrink-0" />
                  <span>Multi-Expert Network</span>
                </div>
                <Link to="/licensing" className="flex items-center gap-2 text-xs font-semibold text-[#E8D4A0] bg-[#162236] border border-[#C9A84C]/30 px-3 py-2 rounded-xl hover:border-[#C9A84C] transition-all">
                  <MapPin className="h-4 w-4 text-[#C9A84C] shrink-0" />
                  <span>Licensed in TX, CA & +7</span>
                </Link>
              </div>

              {/* Social Icon Row */}
              <div className="flex gap-3 pt-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-xl bg-[#162236] border border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#070E1B] hover:scale-110 transition-all duration-300 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Solutions Links */}
            <div>
              <h4 className="font-playfair font-bold text-lg mb-6 text-white border-b border-[#C9A84C]/30 pb-2 inline-block">
                Wealth Solutions
              </h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <a 
                      href={service.href} 
                      className="text-slate-300 hover:text-[#E8D4A0] transition-colors duration-200 text-sm flex items-center gap-1.5"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
                      {service.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Knowledge & Licensing Links */}
            <div>
              <h4 className="font-playfair font-bold text-lg mb-6 text-white border-b border-[#C9A84C]/30 pb-2 inline-block">
                Knowledge & Compliance
              </h4>
              <ul className="space-y-3">
                {resources.map((resource, index) => (
                  <li key={index}>
                    <Link 
                      to={resource.href} 
                      className="text-slate-300 hover:text-[#E8D4A0] transition-colors duration-200 text-sm flex items-center gap-1.5"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
                      {resource.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Direct Contact */}
            <div>
              <h4 className="font-playfair font-bold text-lg mb-6 text-white border-b border-[#C9A84C]/30 pb-2 inline-block">
                Direct Contact
              </h4>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <div>
                    <a href="tel:9032313051" className="text-white font-bold hover:text-[#E8D4A0] transition-colors">
                      (903) 231-3051
                    </a>
                    <div className="text-xs text-slate-400">Direct Advisory Line</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <div>
                    <a href="mailto:info@growandstayrich.com" className="text-white font-bold hover:text-[#E8D4A0] transition-colors">
                      info@growandstayrich.com
                    </a>
                    <div className="text-xs text-slate-400">Direct Client Support</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-white font-bold">Mon - Fri: 9am - 6pm CST</div>
                    <div className="text-xs text-slate-400">Saturday by appointment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Dispatch Banner */}
      <div className="border-t border-slate-800 bg-[#0D1B2A]/90 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h3 className="text-2xl font-bold font-playfair text-white">Join The Weekly Wealth Dispatch</h3>
            <p className="text-slate-300 text-sm">
              Receive tax-free growth blueprints, Living Benefits guides, and economic insights straight to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto pt-2">
              <input
                type="email"
                placeholder="Enter your email address..."
                className="flex-1 px-4 py-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-[#C9A84C] text-sm"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button 
                type="submit" 
                disabled={submitting} 
                className="px-6 py-3 bg-gradient-gold-cta text-[#070E1B] font-bold text-sm rounded-xl shadow-lg hover:opacity-95 transition-all duration-300 shrink-0"
              >
                {submitting ? 'Subscribing…' : 'Subscribe Now'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Regulatory Compliance & Agency Disclosure Block */}
      <div className="border-t border-slate-800 py-6 bg-[#040810]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          <p className="text-[11px] sm:text-xs text-slate-400 leading-relaxed text-center">
            <strong>Disclaimers & Licensing Disclosures:</strong> Grow & Stay Rich operates as an independent financial education and life insurance advisory network (NPN: 21450620). Contracting and licensing portals facilitated through SureLC. Member agency affiliated with Hegemon Group International (HGI) / Integrity Marketing Group network. Licensed to conduct business across Texas (Primary), California, Colorado, Florida, Idaho, Illinois, Kansas, Ohio, and South Carolina.
          </p>
          <p className="text-[11px] sm:text-xs text-slate-400 leading-relaxed text-center">
            Contractual guarantees, including 0% market downside floors and guaranteed lifetime income options, are backed solely by the financial strength and claims-paying ability of the issuing insurance carriers (such as Allianz, Corebridge, Nationwide, Athene, F&G, Lincoln Financial, Mutual of Omaha, Americo, North American, etc.). Life insurance strategies (such as Index Universal Life) and fixed index annuities are insurance products and do not represent direct stock market equity investments.
          </p>
          <p className="text-[10px] text-slate-500 text-center font-mono">
            HQ Regulatory Address: 2019 Holy Unity Ln, St Paul, TX-75098 • Official Advisory Line: (903) 231-3051
          </p>
        </div>
      </div>

      {/* Bottom Legal Navigation Bar */}
      <div className="border-t border-slate-900 py-6 bg-[#02050B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
            <div>
              &copy; {new Date().getFullYear()} Grow & Stay Rich. All rights reserved.
            </div>
            <div className="flex flex-wrap gap-6">
              <Link to="/licensing" className="hover:text-[#E8D4A0] transition-colors duration-200">State Licensing & NPN</Link>
              <Link to="/privacy" className="hover:text-[#E8D4A0] transition-colors duration-200">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#E8D4A0] transition-colors duration-200">Terms of Service</Link>
              <Link to="/disclaimer" className="hover:text-[#E8D4A0] transition-colors duration-200">Regulatory Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}